// Celebration overlay (SC-H4, design §2.5 + D6/D9). Full-screen overlay (mounted on document.body
// like the parent sheet) shown when a mechanic finishes or after mark-done-direct. Amber badge with
// a rotating emoji, title, result line, the 3 SVG faces, Next, "Parent: add a note or photo".
//
// D6 gating:
//  · played === true  (a mechanic was run, incl. plain "I did it!") → Next stays disabled until a face is picked; no skip.
//  · played === false (mark-done-direct)                            → a "skip" link appears; Next enables on a face OR on skip.
// Picking a face sets the rating; skip leaves it null.

import { runConfetti } from './confetti.js';
import { FACE_SVG, FACE_WORD_KEY } from './faces.js';

const T = (k, p) => window.HUD?.T?.(k, p) || k;
const _esc = s => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// opts: { played, title, resultText, badgeEmoji, nextLabel, onNext(rating)->Promise<bool>, onParentAdd(rating) }
// Returns { close() }.
export function showCelebration(opts) {
  const { played, title, resultText, badgeEmoji, nextLabel, onNext, onParentAdd } = opts;
  let rating = null, canProceed = false, busy = false, confettiCtl = null;

  const ov = document.createElement('div');
  ov.className = 'hwp-cel';
  ov.innerHTML =
    `<canvas class="hwp-cel-confetti"></canvas>` +
    `<div class="hwp-cel-badge">${_esc(badgeEmoji)}</div>` +
    `<h2 class="hwp-cel-title ff">${_esc(title)}</h2>` +
    `<p class="hwp-cel-sub">${_esc(resultText)}</p>` +
    `<div class="hwp-cel-faces"></div>` +
    `<div class="hwp-cel-faceslabel">${_esc(T('hw5_how_was_it'))}</div>` +
    `<button type="button" class="hwp-cel-next" disabled>${_esc(nextLabel)}</button>` +
    (played ? '' : `<button type="button" class="hwp-cel-skip">${_esc(T('hw5_skip_rating'))}</button>`) +
    `<button type="button" class="hwp-cel-parent">${_esc(T('hw5_parent_add'))}</button>`;

  const facesEl = ov.querySelector('.hwp-cel-faces');
  const label = ov.querySelector('.hwp-cel-faceslabel');
  const nextBtn = ov.querySelector('.hwp-cel-next');
  const parentBtn = ov.querySelector('.hwp-cel-parent');
  const skipBtn = ov.querySelector('.hwp-cel-skip');

  const enableNext = () => { canProceed = true; nextBtn.disabled = false; nextBtn.classList.add('ready'); };

  [1, 2, 3].forEach(v => {
    const f = document.createElement('button'); f.type = 'button'; f.className = 'hwp-face'; f.innerHTML = FACE_SVG[v];
    f.onclick = () => {
      facesEl.querySelectorAll('.hwp-face').forEach(x => x.classList.remove('pick'));
      f.classList.add('pick'); rating = v; label.textContent = T(FACE_WORD_KEY[v]); enableNext();
    };
    facesEl.appendChild(f);
  });

  if (skipBtn) skipBtn.onclick = () => { if (!busy) enableNext(); }; // mark-done: skip enables Next, rating stays null

  const controller = { close() { if (confettiCtl) confettiCtl.stop(); ov.remove(); } };

  // Next: save (caller) is awaited; disable while in flight so a double-tap can't double-write.
  nextBtn.onclick = async () => {
    if (busy || !canProceed) return;
    busy = true; nextBtn.disabled = true; nextBtn.classList.add('saving');
    let ok = false;
    try { ok = await onNext(rating); } catch (_) { ok = false; }
    if (ok) { controller.close(); }               // caller has navigated; drop the overlay
    else { busy = false; nextBtn.disabled = false; nextBtn.classList.remove('saving'); } // let them retry
  };

  // Parent add: hand the picked rating to the caller, which closes this overlay and opens the sheet.
  parentBtn.onclick = () => { if (busy) return; busy = true; onParentAdd(rating); };

  document.body.appendChild(ov);
  requestAnimationFrame(() => { ov.classList.add('on'); confettiCtl = runConfetti(ov.querySelector('.hwp-cel-confetti')); });
  return controller;
}
